import { css, keyframes } from '@emotion/css';

import { useNoche } from '#components/mobile/budget/travesia/useNoche';
import { colorGavi } from '#components/mobile/travesia/gaviCss';
import { useReducedMotion } from '#hooks/useReducedMotion';

/**
 * `vuela`: Gavi llega con el extracto en el pico. `posada`: espera en la
 * popa. `carga`: las cajas (los movimientos) caen en cubierta. `zarpa`: el
 * barco avanza un poco con la carga a bordo.
 */
export type FaseCorreo = 'vuela' | 'posada' | 'carga' | 'zarpa';

const DIA = {
  cieloA: '#bfe3f3',
  cieloB: '#eef7fb',
  mar: ['#3f8fb8', '#2c7299', '#1f5b7d'],
  casco: '#0f3b3a',
  vela: '#ffffff',
  velaBorde: '#c9d3e3',
  astro: '#ffd77a',
  nube: 'rgba(255,255,255,.75)',
  caja: '#c98d4a',
  cajaBorde: '#8a5a26',
};

const NOCHE = {
  cieloA: '#0d1b33',
  cieloB: '#1a2c4d',
  mar: ['#1d4f73', '#163f5e', '#0f2d45'],
  casco: '#0a2524',
  vela: '#dfe6f2',
  velaBorde: '#8d9ab3',
  astro: '#f3e7b5',
  nube: 'rgba(255,255,255,.08)',
  caja: '#a8743c',
  cajaBorde: '#6b4419',
};

const ola = keyframes({ to: { transform: 'translateX(-200px)' } });
const nube = keyframes({
  from: { transform: 'translateX(-120px)' },
  to: { transform: 'translateX(520px)' },
});
const mecer = keyframes({
  '0%, 100%': { transform: 'rotate(-2deg)' },
  '50%': { transform: 'rotate(2deg) translateY(4px)' },
});
const ondear = keyframes({
  from: { transform: 'skewY(-6deg) scaleX(.9)' },
  to: { transform: 'skewY(6deg) scaleX(1.05)' },
});
const vuelo = keyframes({
  '0%': { transform: 'translate(400px, 20px)' },
  '50%': { transform: 'translate(290px, 30px)' },
  '100%': { transform: 'translate(226px, 138px)' },
});
const aletear = keyframes({
  from: { transform: 'rotate(14deg)' },
  to: { transform: 'rotate(-30deg)' },
});
const rollo = keyframes({
  '0%': { transform: 'translate(450px, 50px)' },
  '50%': { transform: 'translate(340px, 56px)' },
  '100%': { transform: 'translate(276px, 162px)' },
});
const soltar = keyframes({
  from: { transform: 'translate(276px, 162px) rotate(0deg)', opacity: 1 },
  to: { transform: 'translate(190px, 190px) rotate(200deg)', opacity: 0 },
});
const caer = keyframes({
  from: { transform: 'translateY(-40px)', opacity: 0 },
  to: { transform: 'none', opacity: 1 },
});
const zarpar = keyframes({ to: { transform: 'translateX(36px)' } });

const estilos = {
  ola: (s: number, inverso = false) =>
    css({
      animation: `${ola} ${s}s linear infinite${inverso ? ' reverse' : ''}`,
    }),
  nube: (retraso: number) =>
    css({ animation: `${nube} 18s linear ${retraso}s infinite` }),
  mecer: css({
    transformBox: 'fill-box',
    transformOrigin: '50% 90%',
    animation: `${mecer} 3.2s ease-in-out infinite`,
  }),
  bandera: css({
    transformBox: 'fill-box',
    transformOrigin: '0 50%',
    animation: `${ondear} .8s ease-in-out infinite alternate`,
  }),
  zarpa: css({ animation: `${zarpar} 4s ease-in-out forwards` }),
  vuela: css({ animation: `${vuelo} 2.6s cubic-bezier(.4,.1,.3,1) forwards` }),
  ala: css({
    transformBox: 'view-box',
    transformOrigin: '20px 38px',
    animation: `${aletear} .22s ease-in-out infinite alternate`,
  }),
  rollo: css({
    animation: `${rollo} 2.6s cubic-bezier(.4,.1,.3,1) forwards, ${soltar} .8s ease-in 2.6s forwards`,
  }),
  caja: (i: number) =>
    css({
      opacity: 0,
      animation: `${caer} .5s cubic-bezier(.3,1.4,.5,1) ${i * 0.25}s forwards`,
    }),
};

const CAJAS: Array<[number, number, number]> = [
  [58, 88, 13],
  [75, 88, 13],
  [104, 88, 13],
  [66, 76, 12],
  [121, 88, 13],
];

function onda(paso: number, alto: number) {
  let d = `M0 30`;
  for (let x = 0; x < 900; x += paso * 2) {
    d += ` Q${x + paso / 2} ${30 - alto} ${x + paso} 30 T${x + paso * 2} 30`;
  }
  return `${d} V60 H0Z`;
}

type EscenaCorreoProps = { fase: FaseCorreo };

/**
 * El puerto de «Extracto en dos toques»: el barco se mece y Gavi llega
 * volando con el extracto enrollado (dibujo propio, copia de la maqueta).
 * Quieto con «reducir movimiento»: Gavi ya posada y la carga a bordo.
 */
export function EscenaCorreo({ fase }: EscenaCorreoProps) {
  const noche = useNoche();
  const quieta = useReducedMotion();
  const c = noche ? NOCHE : DIA;
  const vuela = fase === 'vuela' && !quieta;
  const conCarga = fase === 'carga' || fase === 'zarpa';
  const anima = (clase: string) => (quieta ? undefined : clase);

  return (
    <svg
      viewBox="0 0 390 280"
      preserveAspectRatio="xMidYMax slice"
      width="100%"
      height={240}
      aria-hidden
      data-testid="escena-correo"
      data-fase={fase}
      style={{ display: 'block' }}
    >
      <defs>
        <linearGradient id="cielo-correo" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={c.cieloA} />
          <stop offset="1" stopColor={c.cieloB} />
        </linearGradient>
      </defs>
      <rect width="390" height="280" fill="url(#cielo-correo)" />
      <circle cx="325" cy="50" r="23" fill={c.astro} opacity={0.95} />
      <rect
        className={anima(estilos.nube(0))}
        x="0"
        y="40"
        width="70"
        height="14"
        rx="7"
        fill={c.nube}
      />
      <rect
        className={anima(estilos.nube(-9))}
        x="160"
        y="78"
        width="46"
        height="14"
        rx="7"
        fill={c.nube}
      />

      <g className={fase === 'zarpa' ? anima(estilos.zarpa) : undefined}>
        <g transform="translate(84 100)">
          <g className={anima(estilos.mecer)}>
            <line
              x1="96"
              y1="10"
              x2="96"
              y2="104"
              stroke={c.casco}
              strokeWidth="3"
            />
            <path
              className={anima(estilos.bandera)}
              d="M96 10 L118 15 L96 20 Z"
              fill="#e8a93b"
            />
            <path
              d="M93 22 L93 98 L38 98 Z"
              fill={c.vela}
              stroke={c.velaBorde}
            />
            <path
              d="M100 26 L100 98 L146 98 Z"
              fill={c.vela}
              stroke={c.velaBorde}
            />
            {conCarga &&
              CAJAS.map(([x, y, h], i) => (
                <rect
                  key={i}
                  className={quieta ? undefined : estilos.caja(i)}
                  x={x}
                  y={y}
                  width="16"
                  height={h}
                  rx="2"
                  fill={c.caja}
                  stroke={c.cajaBorde}
                />
              ))}
            <path d="M22 100 H176 L160 128 H40 Z" fill={c.casco} />
            <text
              x="100"
              y="120"
              textAnchor="middle"
              fontSize="11"
              fontWeight="800"
              fill="#ffd77a"
            >
              RUMBO
            </text>
          </g>
        </g>
      </g>

      {[
        [200, 25, 12, 7, false],
        [220, 35, 14, 5, true],
        [242, 30, 10, 9, false],
      ].map(([y, paso, alto, s, inverso], i) => (
        <g key={i} transform={`translate(-40 ${y})`}>
          <path
            className={anima(estilos.ola(s as number, inverso as boolean))}
            d={onda(paso as number, alto as number)}
            fill={c.mar[i]}
          />
        </g>
      ))}

      {vuela && (
        <g className={estilos.rollo}>
          <rect
            width="30"
            height="12"
            rx="6"
            fill="#fff8e6"
            stroke="#c9a86a"
            strokeWidth="1.5"
          />
          <rect x="22" y="1" width="6" height="10" rx="3" fill="#f0dfb5" />
        </g>
      )}

      <g
        className={vuela ? estilos.vuela : undefined}
        transform={vuela ? undefined : 'translate(226 138)'}
      >
        <svg width="74" height="70" viewBox="0 0 64 60" overflow="visible">
          <path d="M8 40 L1 44 L10 45 Z" fill={colorGavi.gris} />
          <ellipse
            cx="28"
            cy="40"
            rx="19"
            ry="13"
            fill={colorGavi.blanco}
            stroke={colorGavi.borde}
          />
          <path
            d="M26 53 L24 59 M33 53 L35 59"
            stroke={colorGavi.pico}
            strokeWidth="2.6"
            strokeLinecap="round"
          />
          <circle
            cx="42"
            cy="24"
            r="11"
            fill={colorGavi.blanco}
            stroke={colorGavi.borde}
          />
          <circle cx="45.5" cy="21.5" r="2.3" fill={colorGavi.ojo} />
          <circle cx="46.2" cy="20.8" r=".7" fill={colorGavi.brillo} />
          <path d="M51 25 L61 27.5 L51 30 Z" fill={colorGavi.pico} />
          <circle cx="57" cy="28.4" r="1.1" fill={colorGavi.moflete} />
          <g className={vuela ? estilos.ala : undefined}>
            <path
              d="M12 38 Q22 25 40 33 Q31 38 21 47 Z"
              fill={colorGavi.gris}
            />
            <path d="M12 38 L8 34 L16 35 Z" fill={colorGavi.punta} />
          </g>
        </svg>
      </g>
    </svg>
  );
}
